import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { translate } from 'containers/services/translate';
import {
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  FormHelperText,
} from '@material-ui/core';
import { MINOR_VALUE } from 'containers/PreOpeningPage/constants';
import messages from 'containers/PreOpeningPage/messages';
import frLocale from 'date-fns/locale/fr';

import {
  BENEFICIARY,
  EMPTY_BENEFICIARY,
  PARTIES_CLAIM_STATUS,
  VICTIM,
} from 'containers/data-structure';
import { get, isEmpty, isNil, isNull } from 'lodash';

import {
  ageValidation,
  errorBirthYearBeneficiary,
  getAgeBeneficiary,
  getBirthYearBeneficiary,
  isObjectAttributesEmpty,
  minorVictimQualityAyantDroitList,
} from 'containers/PreOpeningPage/utils';
import styled from 'styled-components';

import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import { FormContainer } from 'containers/CourtManagerLegalProceedings/JudgementOnMerits/styled-components';
import { BENEFICIARY_OPTIONS } from 'containers/PreOpeningPage/constants';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import DateFnsUtils from '@date-io/date-fns';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import moment from 'moment-timezone';
import { DATE_PICKET_FORMAT } from 'constants/global';
import * as featureService from 'features/fflip-helpers';
import { VictimType } from 'typings/globalTypes';
import { FEATURES } from 'features/features';
export interface BeneficiaryFormProps {
  intl: IntlShape;
  beneficiary?: any;
  isDrawer: boolean;
  handleDrawerChange?: Function;
  handleSubmit?: Function;
  handleCancel?: Function;
  handleDelete?: Function;
  couldDelete?: boolean;
  inputProps?: any;
  victim: VictimType;
  disableInput?: boolean;
  listing: Listings;
  inputLabelProps?: boolean;
  isOpeningManager?: boolean;
  isCourtMonitoringManagerUpdate?: boolean;
  isLawyer?: boolean;
  isRejected?: boolean;
  isCourtManager?: boolean;
  beneficiaryToUpdate?: any;
  lossDate?: string;
  isCourtManagerBasicManagement?: boolean;
  isDrawerAdd?: boolean;
  withoutPadding?: boolean;
  handleChangeBeneficiaryTransaction?: Function;
  isProvisional?: boolean;
}

const Container = styled.div`
  .align-right {
    text-align: right;
  }
  .button-left-space {
    margin-left: 15px;
  }
  .max-content {
    width: max-content !important;
  }
  .errorSelect {
    color: red;
  }
`;

const BeneficiaryForm: React.FC<BeneficiaryFormProps> = (
  props,
): JSX.Element => {
  const [beneficiary, setBeneficiary] = useState({
    ...EMPTY_BENEFICIARY,
    ...props.beneficiaryToUpdate,
  });
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  useEffect(() => {
    if (props.beneficiaryToUpdate)
      setBeneficiary({
        ...props.beneficiaryToUpdate,
      });
  }, [props.beneficiaryToUpdate]);
  const handleChange = (field: string) => (
    event: React.ChangeEvent<{
      value: any;
    }>,
  ) => {
    const { value } = event.target;
    setBeneficiary({
      ...beneficiary,
      [field]: typeof value === 'string' ? value.replace(',', '.') : value,
    });
    if (props.handleDrawerChange) {
      props.handleDrawerChange({
        ...beneficiary,
        [field]: typeof value === 'string' ? value.replace(',', '.') : value,
      });
    }
    if (props.handleChangeBeneficiaryTransaction) {
      props.handleChangeBeneficiaryTransaction({
        ...beneficiary,
        [field]: typeof value === 'string' ? value.replace(',', '.') : value,
      });
    }
  };
  const handleDateChange = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const dateOfBirth = moment(theDate as Date).format('YYYY-MM-DD');
    if (
      get(beneficiary, 'isFromSagis', false) &&
      featureService.hasFeature(FEATURES.BASIC_MANAGEMENT_REGULATION) &&
      !isNil(get(beneficiary, [BENEFICIARY.AGE], null))
    ) {
      setBeneficiary({
        ...beneficiary,
        [BENEFICIARY.DATE_OF_BIRTH]: dateOfBirth,
      });
    } else {
      setBeneficiary({
        ...beneficiary,
        [BENEFICIARY.DATE_OF_BIRTH]: dateOfBirth,
        [BENEFICIARY.AGE]: String(
          getAgeBeneficiary(
            dateOfBirth,
            props.lossDate,
            get(beneficiary, [BENEFICIARY.AGE]),
          ),
        ),
      });
    }
    if (props.handleDrawerChange) {
      props.handleDrawerChange({
        ...beneficiary,
        [BENEFICIARY.DATE_OF_BIRTH]: dateOfBirth,
        [BENEFICIARY.AGE]: String(
          getAgeBeneficiary(
            dateOfBirth,
            props.lossDate,
            get(beneficiary, [BENEFICIARY.AGE]),
          ),
        ),
      });
    }
  };
  const handleSave = () => {
    if (!isObjectAttributesEmpty(beneficiary)) {
      setBeneficiary(EMPTY_BENEFICIARY);
      if (props.handleSubmit)
        props.handleSubmit({
          ...beneficiary,
          [BENEFICIARY.AGE]: beneficiaryAge,
        });
    }
  };

  const handleCancel = () => {
    if (props.handleCancel) {
      props.handleCancel();
    }
  };

  const handleDelete = () => {
    if (props.handleDelete) {
      props.handleDelete();
    }
  };

  const handleChangeCheckBox = (field: string) => (
    event: React.ChangeEvent<{ value: string; checked: boolean }>,
  ) => {
    const { checked } = event.target;
    setBeneficiary({
      ...beneficiary,
      [field]: checked,
    });
    if (props.handleDrawerChange) {
      props.handleDrawerChange({
        ...beneficiary,
        [field]: checked,
      });
    }
  };
  const isBeneficiaryDateOfBirthValid =
    get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]) &&
    get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]) !== 'Invalid date';

  const beneficiaryAge =
    get(beneficiary, 'isFromSagis', false) &&
    featureService.hasFeature(FEATURES.BASIC_MANAGEMENT_REGULATION) &&
    !isNil(get(beneficiary, [BENEFICIARY.AGE], null))
      ? get(beneficiary, [BENEFICIARY.AGE], null)
      : getAgeBeneficiary(
          get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]),
          props.lossDate,
          get(beneficiary, [BENEFICIARY.AGE]),
        );

  const decisionVictim =
    !isNull(get(props.victim, 'decision')) &&
    get(props.victim, 'decision.status') !== PARTIES_CLAIM_STATUS.VALIDATED;
  return (
    <Container
      className={
        (props.disableInput || props.isDrawer || props.isDrawerAdd) &&
        !props.isOpeningManager &&
        !(props.isLawyer && props.isRejected) &&
        !props.isCourtManager // apply this style when on readonly mode or on drawer
          ? ''
          : 'accordion-form-container'
      }
      style={{ width: '100%' }}
    >
      <Grid
        container
        item
        spacing={props.isDrawer || props.isDrawerAdd ? 3 : 2}
        style={{
          padding:
            (props.isDrawer && !props.withoutPadding) ||
            (props.isDrawerAdd && !props.withoutPadding)
              ? 12
              : 0,
        }}
        direction={props.isDrawer || props.isDrawerAdd ? 'column' : 'row'}
      >
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <Grid item xs={props.isDrawer || props.isDrawerAdd ? 12 : 3}>
            <FormControl fullWidth>
              <InputLabel
                ref={inputLabel}
                id="demo-simple-select-outlined-label"
              >
                {translate({ ...messages.qualityBeneficiary }, props)}
              </InputLabel>
              <Select
                fullWidth
                onChange={handleChange(BENEFICIARY.BENEFICIARY_TYPE)}
                name={BENEFICIARY.BENEFICIARY_TYPE}
                value={get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], '')}
                labelWidth={60}
                disabled={
                  props.disableInput &&
                  !props.isOpeningManager &&
                  !(props.isLawyer && props.isRejected) &&
                  !props.isCourtManager
                }
                error={
                  isNil(
                    get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], null),
                  ) && !props.isProvisional
                }
              >
                {!isEmpty(get(props.listing, 'qualityAyantDroit')) &&
                  (get(props.victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE
                    ? minorVictimQualityAyantDroitList(
                        get(props.listing, 'qualityAyantDroit'),
                      )
                    : get(props.listing, 'qualityAyantDroit')
                  ).map((ayantDroit: SagisConfig) => (
                    <MenuItem
                      value={ayantDroit.ItemKey}
                      key={ayantDroit.ItemKey}
                    >
                      {ayantDroit.ItemDescription}
                    </MenuItem>
                  ))}
              </Select>
              {isNil(get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], null)) &&
                !props.isProvisional && (
                  <FormHelperText className="errorSelect">
                    {translate(messages.beneficiaryKey, props)}
                  </FormHelperText>
                )}
            </FormControl>
          </Grid>
        )}
        <Grid item xs={props.isDrawer || props.isDrawerAdd ? 12 : 3}>
          <TextField
            fullWidth
            label={translate({ ...messages.victimFirstName }, props)}
            name={BENEFICIARY.FIRST_NAME}
            value={get(beneficiary, [BENEFICIARY.FIRST_NAME]) || ''}
            onChange={handleChange(BENEFICIARY.FIRST_NAME)}
            InputProps={props.inputProps}
            disabled={
              props.disableInput &&
              !props.isOpeningManager &&
              !(props.isLawyer && props.isRejected) &&
              !props.isCourtManager
            }
            InputLabelProps={{
              shrink: props.inputLabelProps,
            }}
            error={false}
            className={props.isDrawer || props.isDrawerAdd ? '' : 'input-4'}
            helperText=""
          />
        </Grid>
        <Grid item xs={props.isDrawer || props.isDrawerAdd ? 12 : 3}>
          <TextField
            fullWidth
            label={translate({ ...messages.victimLastName }, props)}
            name={BENEFICIARY.LAST_NAME}
            value={get(beneficiary, [BENEFICIARY.LAST_NAME]) || ''}
            onChange={handleChange(BENEFICIARY.LAST_NAME)}
            InputProps={props.inputProps}
            disabled={
              props.disableInput &&
              !props.isOpeningManager &&
              !(props.isLawyer && props.isRejected) &&
              !props.isCourtManager
            }
            InputLabelProps={{
              shrink: props.inputLabelProps,
            }}
            error={false}
            helperText=""
            className={props.isDrawer || props.isDrawerAdd ? '' : 'input-4'}
          />
        </Grid>
        <Grid item xs={props.isDrawer || props.isDrawerAdd ? 12 : 3}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
            <KeyboardDatePicker
              autoOk
              fullWidth
              variant="inline"
              format={DATE_PICKET_FORMAT}
              label={translate({ ...messages.dateOfBirth }, props)}
              name={BENEFICIARY.DATE_OF_BIRTH}
              id={BENEFICIARY.DATE_OF_BIRTH}
              minDate={
                get(beneficiary, 'isFromSagis', false)
                  ? new Date(
                      `${getBirthYearBeneficiary(
                        props.lossDate,
                        get(beneficiary, [BENEFICIARY.AGE]),
                      )}-01-01`,
                    )
                  : undefined
              }
              maxDate={
                get(beneficiary, 'isFromSagis', false)
                  ? new Date(
                      `${getBirthYearBeneficiary(
                        props.lossDate,
                        get(beneficiary, [BENEFICIARY.AGE]),
                      )}-12-31`,
                    )
                  : undefined
              }
              value={dateStringToLocalDate(
                beneficiary[BENEFICIARY.DATE_OF_BIRTH],
              )}
              className={props.isDrawer || props.isDrawerAdd ? '' : 'input-4'}
              onChange={handleDateChange}
              InputProps={props.inputProps}
              InputLabelProps={{
                shrink: props.inputLabelProps,
              }}
              disabled={props.isCourtManagerBasicManagement}
              error={
                (!isBeneficiaryDateOfBirthValid ||
                  dateStringToLocalDate(
                    beneficiary[BENEFICIARY.DATE_OF_BIRTH],
                  ) === 'Invalid date' ||
                  (get(beneficiary, 'isFromSagis', false) &&
                    errorBirthYearBeneficiary(
                      get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]),
                      getBirthYearBeneficiary(
                        props.lossDate,
                        get(beneficiary, [BENEFICIARY.AGE]),
                      ),
                    ))) &&
                !props.isProvisional
              }
              helperText={
                ((!isBeneficiaryDateOfBirthValid &&
                  translate(
                    messages.beneficiaryDateValidationMessage,
                    props,
                  )) ||
                  (dateStringToLocalDate(
                    beneficiary[BENEFICIARY.DATE_OF_BIRTH],
                  ) === 'Invalid date' &&
                    translate(messages.invalidDate, props)) ||
                  (get(beneficiary, 'isFromSagis', false) &&
                    errorBirthYearBeneficiary(
                      get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]),
                      getBirthYearBeneficiary(
                        props.lossDate,
                        get(beneficiary, [BENEFICIARY.AGE]),
                      ),
                    ) &&
                    translate(
                      messages.beneficiaryDateValidationMessage,
                      props,
                    ))) &&
                !props.isProvisional
              }
            />
          </MuiPickersUtilsProvider>
        </Grid>
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <Grid item xs={props.isDrawer || props.isDrawerAdd ? 12 : 3}>
            <TextField
              fullWidth
              label={translate({ ...messages.beneficiaryAge }, props)}
              name={BENEFICIARY.AGE}
              value={beneficiaryAge}
              onChange={handleChange(BENEFICIARY.AGE)}
              InputProps={props.inputProps}
              disabled={true}
              InputLabelProps={{
                shrink: props.inputLabelProps,
              }}
              className="input-4"
              error={ageValidation(
                get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], ''),
                beneficiaryAge,
              )}
              helperText={
                ageValidation(
                  get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], ''),
                  beneficiaryAge,
                ) &&
                (get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], '') === 3
                  ? translate(messages.agePartnerValidation, props)
                  : translate(messages.ageBeneficiaryValidation, props))
              }
            ></TextField>
          </Grid>
        )}
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <Grid item xs={props.isDrawer || props.isDrawerAdd ? 12 : 3}>
            <TextField
              fullWidth
              label={translate({ ...messages.victimFrais }, props)}
              type="number"
              name={BENEFICIARY.FRAIS}
              value={get(beneficiary, [BENEFICIARY.FRAIS], '')}
              onChange={handleChange(BENEFICIARY.FRAIS)}
              InputProps={props.inputProps}
              disabled={
                props.disableInput &&
                !props.isOpeningManager &&
                !(props.isLawyer && props.isRejected) &&
                !props.isCourtManager
              }
              className="input-4"
              InputLabelProps={{
                shrink: get(beneficiary, [BENEFICIARY.FRAIS]),
              }}
              error={false}
              helperText=""
            ></TextField>
          </Grid>
        )}
      </Grid>

      {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
        <div style={{ marginLeft: !props.withoutPadding ? '10px' : '0px' }}>
          <FormContainer>
            <FormGroup>
              <FormControlLabel
                key={0}
                control={
                  <Checkbox
                    checked={get(
                      beneficiary,
                      [BENEFICIARY.HANDICAP_INDICATOR],
                      false,
                    )}
                    color="primary"
                    disabled={
                      props.disableInput &&
                      !props.isOpeningManager &&
                      !(props.isLawyer && props.isRejected) &&
                      !props.isCourtManager
                    }
                    onChange={handleChangeCheckBox(
                      BENEFICIARY.HANDICAP_INDICATOR,
                    )}
                  />
                }
                label={BENEFICIARY_OPTIONS[0]}
              />
              <FormControlLabel
                key={1}
                control={
                  <Checkbox
                    checked={get(
                      beneficiary,
                      [BENEFICIARY.DEPENDENT_INDICATOR],
                      false,
                    )}
                    color="primary"
                    disabled={
                      props.disableInput &&
                      !props.isOpeningManager &&
                      !(props.isLawyer && props.isRejected) &&
                      !props.isCourtManager
                    }
                    onChange={handleChangeCheckBox(
                      BENEFICIARY.DEPENDENT_INDICATOR,
                    )}
                  />
                }
                label={BENEFICIARY_OPTIONS[1]}
              />
            </FormGroup>
          </FormContainer>
        </div>
      )}
      {((!props.disableInput && !props.isDrawer) ||
        (props.isOpeningManager && !props.isDrawer) ||
        (props.isLawyer && props.isRejected && !props.isDrawer) ||
        (props.isCourtManager && !props.isDrawer)) && (
        <div className="align-right">
          <SecondaryButton
            className="cancelButton"
            onClick={handleCancel}
            title={translate({ ...messages.cancelBtn }, props)}
          />
          <PrimaryButton
            className="button-left-space"
            onClick={handleSave}
            title={translate({ ...messages.saveBtn }, props)}
            disabled={
              isNil(get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE], null)) ||
              ageValidation(
                get(beneficiary, [BENEFICIARY.BENEFICIARY_TYPE]),
                beneficiaryAge,
              ) ||
              !get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]) ||
              get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]) ===
                'Invalid date' ||
              (get(beneficiary, 'isFromSagis', false) &&
                errorBirthYearBeneficiary(
                  get(beneficiary, [BENEFICIARY.DATE_OF_BIRTH]),
                  getBirthYearBeneficiary(
                    props.lossDate,
                    get(beneficiary, [BENEFICIARY.AGE]),
                  ),
                ))
            }
          />
        </div>
      )}

      {props.disableInput && !props.isDrawer && props.couldDelete && (
        <div className="align-right">
          <SecondaryButton
            className="deleteButton"
            onClick={handleDelete}
            title={translate({ ...messages.deleteBtn }, props)}
          />
        </div>
      )}
    </Container>
  );
};

export default injectIntl(BeneficiaryForm);
